import React, { useCallback } from 'react'
import { useRouter, loginRoute, support } from '@src/routes'
import { FormattedMessage, useIntl } from 'react-intl'
import { MutationFunction } from 'react-apollo'
import { makeStyles } from '@material-ui/core/styles'
import { Button, Theme, List, ListItem, Typography } from '@material-ui/core'
import {
  Formik,
  FormikHelpers,
  FormikProps,
  Form,
  FormikConfig,
  Field,
} from 'formik'
import { useValidators } from '@utils/validators'
import {
  ForgotPasswordComponent,
  ForgotPasswordMutation,
  ForgotPasswordMutationVariables,
} from '@src/mutations/ForgotPassword.generated'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import { OriginalError, extractData } from '@utils/errorHandler'
import messages from '@utils/messages'
import { CHINA_WEBHELP_EMAIL } from '@src/routes/Configurators/utils/constants'
import { sendSignInInteractionEvents } from '@src/utils/analytics'
import countriesMap from '@localization/countries.json'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'

// Forgot Password Form Styles
export const useStyles = makeStyles((theme: Theme) => ({
  vr8: {
    marginBottom: theme.spacing(8),
  },
  listItemRoot: {
    display: 'block',
    padding: 0,
    marginBottom: theme.spacing(4),
  },
  input: {
    [theme.breakpoints.up('sm')]: {
      maxWidth: 400,
    },
  },
  pointerCursor: {
    cursor: 'pointer',
  },
  formButton: {
    marginBottom: theme.spacing(3),
    marginRight: theme.spacing(4),
    minWidth: '100%',
    [theme.breakpoints.up('sm')]: {
      minWidth: 166,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
}))

// Form Values Types
interface ForgotPasswordFormValues {
  email: string
  userName: string
}

interface ForgotPasswordVariables {
  showUserName: boolean
  showSuccessMsg: boolean
}

interface ForgotPasswordFormProps {
  variables: ForgotPasswordVariables
  setVariables: React.Dispatch<React.SetStateAction<ForgotPasswordVariables>>
  setErrorMsg: React.Dispatch<React.SetStateAction<string>>
}

// Initial Form Values
const INITIAL_VALUES = {
  email: '',
  userName: '',
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  variables,
  setVariables,
  setErrorMsg,
}) => {
  const classes = useStyles()
  const intl = useIntl()
  const router = useRouter()
  const { requiredEmail, requiredUserName } = useValidators()
  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)

  const listItemClasses = {
    root: classes.listItemRoot,
  }

  const cancelBtnTxt = variables.showSuccessMsg
    ? messages.RESEND_EMAIL
    : messages.CANCEL
  const submitBtnTxt = variables.showSuccessMsg
    ? messages.LOGIN
    : messages.SEND_EMAIL

  const handleForgotPassword = useCallback(
    (
      forgotPassword: MutationFunction<
        ForgotPasswordMutation,
        ForgotPasswordMutationVariables
      >
    ): FormikConfig<ForgotPasswordFormValues>['onSubmit'] =>
      async (
        { email, userName }: ForgotPasswordFormValues,
        actions: FormikHelpers<ForgotPasswordFormValues>
      ) => {
        const { setSubmitting, setStatus } = actions

        if (variables.showSuccessMsg) {
          router.push(loginRoute.index())
        } else {
          try {
            const res = await forgotPassword({
              variables: { email, userName },
            })
            const response = res.data && res.data.forgotPassword
            if (
              response &&
              response.message &&
              response.message === 'USER_EMAIL_TIED_TO_MULTIPLE_PROFILES'
            ) {
              //multiple email error
              setVariables({ showUserName: true, showSuccessMsg: false })
              setErrorMsg(
                intl.formatMessage(
                  messages.USER_EMAIL_TIED_TO_MULTIPLE_PROFILES
                )
              )
              return setSubmitting(false)
            }
            // Password reset successful
            if (
              response &&
              response.message &&
              response.message === 'PASSWORD_RESET_SUCCESSFUL'
            ) {
              sendSignInInteractionEvents({
                action: 'send password reset email',
                section: 'forget password',
                component: 'body',
                element_type: 'button',
                element_text: 'send email',
              })
              setVariables((variables) => ({
                showSuccessMsg: true,
                showUserName: variables.showUserName,
              }))

              setErrorMsg('')
              setStatus(
                intl.formatMessage(messages.PASSWORD_RESET_MAIL_SEND, {
                  email,
                  linebreak: <br />,
                  customer_service: isChinaUser ? (
                    <a
                      tabIndex={0}
                      className={classes.pointerCursor}
                      href={`mailto:${CHINA_WEBHELP_EMAIL}`}
                    >
                      {' '}
                      {intl.formatMessage(messages.CUSTOMER_SUPPORT)}
                    </a>
                  ) : (
                    <a
                      tabIndex={0}
                      rel="noreferrer"
                      target="_blank"
                      className={classes.pointerCursor}
                      href={support.customerSupport()}
                    >
                      {' '}
                      {intl.formatMessage(messages.CUSTOMER_SUPPORT)}
                    </a>
                  ),
                })
              )
              return setSubmitting(false)
            }

            setErrorMsg(intl.formatMessage(messages.GENERIC_ERROR))
            setSubmitting(false)
          } catch (error) {
            const { errors } = extractData(error as OriginalError)
            let errorMsg = intl.formatMessage(messages.GENERIC_ERROR)
            if (errors.length > 0) {
              const { code } = errors[0]
              if (code === 'USER_INVALID_USERNAME_OR_EMAIL') {
                //STRAT-10067 for an invalid email don't throw error
                setVariables(() => ({
                  showSuccessMsg: true,
                  showUserName: false,
                }))
                setErrorMsg('')
                setStatus(
                  intl.formatMessage(messages.PASSWORD_RESET_MAIL_SEND, {
                    email,
                    linebreak: <br />,
                    customer_service: isChinaUser ? (
                      <a
                        tabIndex={0}
                        className={classes.pointerCursor}
                        href={`mailto:${CHINA_WEBHELP_EMAIL}`}
                      >
                        {' '}
                        {intl.formatMessage(messages.CUSTOMER_SUPPORT)}
                      </a>
                    ) : (
                      <a
                        tabIndex={0}
                        rel="noreferrer"
                        target="_blank"
                        className={classes.pointerCursor}
                        href={support.customerSupport()}
                      >
                        {' '}
                        {intl.formatMessage(messages.CUSTOMER_SUPPORT)}
                      </a>
                    ),
                  })
                )
              } else {
                errorMsg = messages[code]
                  ? intl.formatMessage(messages[code])
                  : intl.formatMessage(messages.GENERIC_ERROR)

                setErrorMsg(errorMsg)
              }
            }

            setSubmitting(false)
          }
        }
      },
    // TODO: need a test before we refactor these dependencies

    [intl, setVariables, variables]
  )

  return (
    <ForgotPasswordComponent>
      {(forgotPassword) => (
        <Formik
          initialValues={INITIAL_VALUES}
          onSubmit={handleForgotPassword(forgotPassword)}
        >
          {(formikBag: FormikProps<ForgotPasswordFormValues>) => (
            <Form>
              <List component="div" disablePadding className={classes.vr8}>
                {!!formikBag.status && (
                  <ListItem component="div" classes={listItemClasses}>
                    <Typography
                      component="p"
                      variant="body2"
                      className={classes.vr8}
                      data-scrub
                    >
                      {formikBag.status}
                    </Typography>
                  </ListItem>
                )}

                {!variables.showSuccessMsg ? (
                  <ListItem component="div" classes={listItemClasses}>
                    <Field
                      name="email"
                      className={classes.input}
                      validate={requiredEmail}
                      component={LiquidInputAdapter}
                      label={<FormattedMessage {...messages.EMAIL_ADDRESS} />}
                      filled
                      inputProps={{
                        'data-scrub': 'true',
                        autoComplete: 'username',
                      }}
                    />
                  </ListItem>
                ) : null}
                {variables.showUserName && !variables.showSuccessMsg ? (
                  <ListItem component="div" classes={listItemClasses}>
                    <Field
                      name="userName"
                      className={classes.input}
                      validate={requiredUserName}
                      component={LiquidInputAdapter}
                      label={
                        <FormattedMessage
                          {...messages.USER_NAME_FORGOT_PASSWORD}
                        />
                      }
                      filled
                      inputProps={{
                        'data-scrub': 'true',
                        autoComplete: 'username',
                      }}
                    />
                  </ListItem>
                ) : null}
              </List>

              <Button
                onClick={() => {
                  if (variables.showSuccessMsg) {
                    //Handles RESEND_EMAIL Button click
                    formikBag.setStatus('')
                    setVariables({
                      showUserName: false,
                      showSuccessMsg: false,
                    })
                  } else {
                    //Handles CANCEL button click
                    router.push(loginRoute.index())
                  }
                }}
                variant="outlined"
                color="primary"
                size="large"
                className={classes.formButton}
              >
                <FormattedMessage {...cancelBtnTxt} />
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={formikBag.isSubmitting}
                data-testid="submit-button"
                id="submit-button"
                className={classes.formButton}
              >
                <FormattedMessage {...submitBtnTxt} />
              </Button>
            </Form>
          )}
        </Formik>
      )}
    </ForgotPasswordComponent>
  )
}

export default ForgotPasswordForm
